// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-8 — "Send for signature" for a completed Plan of Treatment, shared by POT classic's dashboard and the
// OwlRx worklist (each renders it in its own look). The server's signing plan decides the paths (one rule:
// mode × DocuSign set up × the physician has an OwlRx login); this module only walks them:
//   choose  — the physician has a login: "Sign in OwlRx" (their provider portal) or DocuSign, where allowed
//   edit    — DocuSign: the physician's name + email, prefilled from §14
//   blocked — neither path is open; the words say why and what to do
import { useEffect, useState } from 'react'
import { sendCareRxPotForSignature } from '../../api/client'
import { esignErrorText } from '../../api/esignErrors'
import type { PotSigningPlan } from '../../api/inappEsign'
import { useCreateInAppPot, usePotSigningPlan } from '../../hooks/queries/inappEsign'
import { isValidEmail, readPhysician } from './carerxPotDashboardLogic'

export type PotSendPhase = 'loading' | 'choose' | 'confirmInApp' | 'edit' | 'confirm' | 'sending' | 'ok' | 'fail' | 'blocked'

const same = (a: string, b: string) => a.toLowerCase().replace(/^dr\.?\s+/, '').replace(/[^a-z]/g, '') === b.toLowerCase().replace(/^dr\.?\s+/, '').replace(/[^a-z]/g, '')

/** The words the dialog shows for a plan (pure — unit-tested). */
export function potSigningWords(plan: PotSigningPlan): { physician: string; inAppTitle: string; inAppBody: string; mismatch: string | null; blocked: string | null } {
  const physician = plan.physician?.name ?? 'The prescriber'
  const s14 = plan.section14_physician
  const blocked = plan.blocked === 'physician_has_no_login'
    ? `${physician} has no OwlRx login, and this pharmacy signs in app only. Invite ${physician} to OwlRx, or ask your administrator to switch Signing to Both or DocuSign.`
    : plan.blocked === 'physician_has_no_login_and_docusign_not_set_up'
      ? `${physician} has no OwlRx login, and DocuSign is not set up for this pharmacy. Invite ${physician} to OwlRx, or ask your administrator to set up DocuSign.`
      : plan.blocked === 'docusign_not_set_up' ? 'DocuSign is not set up for this pharmacy — ask your administrator (SaaS admin → CareRx → Signing).'
        : plan.blocked === 'document_not_rendered' ? 'The POT document has not been generated yet.'
          : plan.blocked === 'prescriber_missing' ? 'The therapy has no prescriber — set the prescriber on the enrollment before sending the POT.'
            : plan.blocked ? 'This POT cannot be sent for signature right now.' : null
  return {
    physician,
    inAppTitle: `Sign in OwlRx — ${physician} has an OwlRx login`,
    inAppBody: `${physician} signs in their provider portal after re-entering their password and authenticator code. Nothing is emailed.`,
    mismatch: s14 && plan.physician?.name && !same(s14, plan.physician.name)
      ? `Step 14 names ${s14}; the POT goes to the prescriber of record, ${plan.physician.name}.` : null,
    blocked,
  }
}

export function usePotSendFlow(row: { visit_id: string; document_id: string | null; schema_code?: string | null }) {
  const planQ = usePotSigningPlan(row.visit_id)
  const createInApp = useCreateInAppPot()
  const [phase, setPhase] = useState<PotSendPhase>('loading')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [result, setResult] = useState<{ envelope?: string; error?: string; channel?: 'owlrx' | 'docusign' }>({})

  // §14's physician prefills the DocuSign path (a convenience — the user can type over it)
  useEffect(() => {
    let alive = true
    readPhysician(row.visit_id, row.schema_code)
      .then(p => { if (alive) { setName(p.name); setEmail(p.email) } })
      .catch(() => { /* prefill is a convenience — the user types the physician in by hand */ })
    return () => { alive = false }
  }, [row.visit_id, row.schema_code])
  // the first step follows the plan once it loads
  useEffect(() => {
    if (phase !== 'loading') return
    if (planQ.isError) { setResult({ error: esignErrorText(planQ.error) }); setPhase('fail'); return }
    const p = planQ.data
    if (!p) return
    setPhase(p.in_app ? 'choose' : p.docusign ? 'edit' : 'blocked')
  }, [planQ.data, planQ.isError, planQ.error, phase])

  const plan = planQ.data ?? null
  const words = plan ? potSigningWords(plan) : null
  const docuSignValid = !!plan?.docusign && name.trim().length > 0 && isValidEmail(email)

  const sendDocuSign = async () => {
    if (!row.document_id) { setResult({ error: 'The POT document has not been generated yet.' }); setPhase('fail'); return }
    setPhase('sending')
    try {
      const r = await sendCareRxPotForSignature(row.document_id, { recipient_email: email.trim(), recipient_name: name.trim() })
      setResult({ envelope: r.envelope_id, channel: 'docusign' }); setPhase('ok')
    } catch (e) { setResult({ error: esignErrorText(e) }); setPhase('fail') }
  }
  const sendInApp = async () => {
    const doc = plan?.document_id ?? row.document_id
    if (!doc) { setResult({ error: 'The POT document has not been generated yet.' }); setPhase('fail'); return }
    setPhase('sending')
    try {
      const r = await createInApp.mutateAsync(doc)
      setResult({ envelope: r.envelope_id, channel: 'owlrx' }); setPhase('ok')
    } catch (e) { setResult({ error: esignErrorText(e) }); setPhase('fail') }
  }
  return { plan, words, phase, setPhase, name, setName, email, setEmail, docuSignValid, result, sendDocuSign, sendInApp }
}
